"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  InputGroup,
  InputGroupText,
  Spinner,
  Alert,
  ListGroup,
  ListGroupItem,
} from "reactstrap"
import { FaSearch, FaUser, FaUndo } from "react-icons/fa"
import { toast } from "react-toastify"
import { StaffCarService } from "../../../../Service/StaffCarService"
import type { PreviousStaffMember, TokenData } from "./types"

interface Props {
  isOpen: boolean
  toggle: () => void
  token: string
  tokenData: TokenData
  onSuccess: () => void
}

const PreviousStaffModal: React.FC<Props> = ({ isOpen, toggle, token, tokenData, onSuccess }) => {
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [search, setSearch] = useState("")
  const [previousStaff, setPreviousStaff] = useState<PreviousStaffMember[]>([])
  const [selected, setSelected] = useState<Set<number>>(new Set())

  const fetchPreviousStaff = async (searchTerm = "") => {
    setLoading(true)
    try {
      const response = await StaffCarService.getPreviousStaff(token, searchTerm)
      setPreviousStaff(response.previous_staff)
    } catch (error) {
      console.error("Failed to load previous staff:", error)
      toast.error("Failed to load previous staff members")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      setSelected(new Set())
      setSearch("")
      fetchPreviousStaff()
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const timeout = setTimeout(() => fetchPreviousStaff(search), 350)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const toggleSelect = (personId: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(personId)) {
        next.delete(personId)
      } else {
        next.add(personId)
      }
      return next
    })
  }

  const handleConfirm = async () => {
    if (selected.size === 0) {
      toast.info("Select at least one staff member to bring back")
      return
    }
    setSubmitting(true)
    try {
      const response = await StaffCarService.confirmPreviousStaff(token, Array.from(selected))
      if (response.created_count > 0) {
        toast.success(`${response.created_count} staff member(s) confirmed for this exhibition`)
      }
      if (response.skipped_count > 0) {
        toast.info(`${response.skipped_count} staff member(s) were already registered`)
      }
      if (response.over_limit_count > 0) {
        toast.warning(
          `${response.over_limit_count} staff exceed your allotted ${response.max_staff} slots — the extra will be charged during badge printing.`,
        )
      }
      onSuccess()
      toggle()
    } catch (error) {
      console.error("Failed to confirm previous staff:", error)
      toast.error("Failed to confirm selected staff. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="modal-dialog-centered">
      <ModalHeader toggle={toggle} className="bg-primary text-white">
        <FaUndo className="me-2" />
        Bring Back Previous Staff
      </ModalHeader>
      <ModalBody className="p-4">
        <Alert color="info" className="mb-3">
          These are people who represented <strong>{tokenData.company_name}</strong> in a previous
          exhibition. Select who is coming back for this exhibition — this avoids re-entering their
          details and creating duplicate records. You can still register additional new staff separately;
          registration is not blocked once your allotted slots are filled, but any extra staff will be
          charged during badge printing.
        </Alert>

        <InputGroup className="mb-3">
          <InputGroupText>
            <FaSearch />
          </InputGroupText>
          <Input
            placeholder="Search by name or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-3 text-muted mb-0">Loading previous staff...</p>
          </div>
        ) : previousStaff.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <FaUser size={40} className="mb-3 opacity-50" />
            <p className="mb-0">No previous staff found for this company.</p>
          </div>
        ) : (
          <ListGroup style={{ maxHeight: 360, overflowY: "auto" }}>
            {previousStaff.map((person) => (
              <ListGroupItem
                key={person.person_id}
                tag="label"
                className="d-flex align-items-center gap-3"
                style={{ cursor: "pointer" }}
              >
                <Input
                  type="checkbox"
                  checked={selected.has(person.person_id)}
                  onChange={() => toggleSelect(person.person_id)}
                  className="mt-0"
                  style={{ position: "static", marginLeft: 0 }}
                />
                <div className="flex-grow-1">
                  <div className="fw-medium text-dark">
                    {person.first_name} {person.last_name}
                  </div>
                  <div className="small text-muted">
                    {person.phone || "No phone provided"}
                    {person.last_exhibition && (
                      <>
                        <span className="mx-2">•</span>
                        Last at: {person.last_exhibition}
                      </>
                    )}
                  </div>
                </div>
              </ListGroupItem>
            ))}
          </ListGroup>
        )}
      </ModalBody>
      <ModalFooter className="bg-light">
        <Button color="secondary" onClick={toggle} disabled={submitting}>
          Cancel
        </Button>
        <Button color="primary" onClick={handleConfirm} disabled={submitting || selected.size === 0}>
          {submitting && <Spinner size="sm" className="me-2" />}
          Confirm {selected.size > 0 ? `(${selected.size})` : ""}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default PreviousStaffModal
